import { useGetJobsiteConfigZonesQuery } from 'apollo/generated/client-operations';
import { LoadingError } from 'components/loadingError';
import {
  JobsiteConfigTabApi,
  JobsiteConfigTabProps,
} from 'containers/jobsiteConfiguration/types';
import { GeofencingTab } from 'containers/jobsiteConfiguration/tabs/geofencing';
import React from 'react';
import { ZonesPanel } from './ZonesPanel';

/**
 * The Site Layout tab for a site without the layout designer
 * (`site-layout-ui-enabled` off): geofencing and gateways, then the read-only
 * list of the site's zones, as the tab was before the designer.
 */
export function LegacySiteLayoutTab(
  props: JobsiteConfigTabProps,
): React.ReactElement {
  const { jobsiteId, onTabApiChange } = props;
  const {
    data,
    loading,
    error,
    refetch: refetchZones,
  } = useGetJobsiteConfigZonesQuery({
    variables: { jobsiteId },
    fetchPolicy: 'network-only',
  });
  const zones = data?.getJobsite?.zones;

  // The header's reload refreshes the zones list with the geofencing form, as
  // one query did on the tab before the designer.
  // Geofencing also reports its form's `submit` on its own, without a reload,
  // so only a reported reload is wrapped.
  const onGeofencingTabApiChange = React.useCallback(
    (api: Partial<JobsiteConfigTabApi>): void =>
      onTabApiChange?.(
        api.refetchData
          ? {
              ...api,
              refetchData: (): void => {
                api.refetchData();
                refetchZones();
              },
            }
          : api,
      ),
    [onTabApiChange, refetchZones],
  );

  return (
    <>
      <GeofencingTab {...props} onTabApiChange={onGeofencingTabApiChange} />
      {error && <LoadingError error={error} />}
      {!loading && zones && <ZonesPanel zones={zones} />}
    </>
  );
}
